import { createFileRoute } from "@tanstack/react-router";
import { LangLink as Link } from "@/components/LangLink";
import { PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import heroFacility from "@/assets/hero-facility.jpg";
import pururuca from "@/assets/pururuca.png";

import t8a from "@/assets/t8a.png";
import t8b from "@/assets/t8b.png";
import t8c from "@/assets/t8c.png";
import propulsionPng from "@/assets/propulsion.png";
import chemicalsPng from "@/assets/chemicals.png";
import ftlCta from "@/assets/cta-ftl.png";
import electronicPng from "@/assets/eletronic.png";
import cubesatsPng from "@/assets/cubesats.png";
import heroVideo from "@/assets/video/hero.mp4";
import mlbrVideo from "@/assets/video/mlbr_missao.mp4";
import verticalTechVideo from "@/assets/video/vertical-capability.mp4";
import ctaImg from "@/assets/cta.png";
import { CountUpNumber } from "@/components/CountUpNumber";
import { SplitSection } from "@/components/SplitSection";
import { CtaSection } from "@/components/CtaSection";
import liquidEngineImage from "@/assets/liquid-rocket-engine-tested.png";
import differentCombustionImage from "@/assets/different-combustion.png";
import cumulativeImage from "@/assets/cumulative-bipropellant.png";

export const Route = createFileRoute("/$lang/")({
  head: () => ({
    meta: [
      { title: i18n.t("home:meta.title", "BIZU Space — Space Technologies for the space industry") },
      {
        name: "description",
        content: i18n.t(
          "home:meta.description",
          "BIZU Space is a vertical developer of rocket and satellite technologies for the space industry — propulsion, electronics, chemicals and CubeSat deployers.",
        ),
      },
      {
        property: "og:title",
        content: i18n.t("home:meta.ogTitle", "BIZU Space — Space Technologies for the space industry"),
      },
      {
        property: "og:description",
        content: i18n.t(
          "home:meta.ogDescription",
          "Vertical developer of rocket and satellite technologies for the space industry.",
        ),
      },
      { property: "og:url", content: "/" },
      { property: "og:image", content: heroFacility },
      { property: "twitter:image", content: heroFacility },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/" }],
  }),
  component: Home,
});

const partnerLogos = Object.values(
  import.meta.glob("/src/assets/partners/*.{png,jpg,jpeg,svg,webp}", {
    eager: true,
    import: "default",
  }),
) as string[];

function Home() {
  const { t } = useTranslation("home");

  const verticals = [
    {
      name: t("verticals.propulsion.name", "Propulsion Systems"),
      desc: t("verticals.propulsion.desc", "Liquid bipropellant engines, RCS thrusters and OTV propulsion."),
      img: propulsionPng,
      link: "/category/propulsion-systems",
    },
    {
      name: t("verticals.chemicals.name", "Chemicals"),
      desc: t("verticals.chemicals.desc", "In-house high-concentration hydrogen peroxide (HTP) production."),
      img: chemicalsPng,
      link: "/category/chemicals",
    },
    {
      name: t("verticals.electronics.name", "Electronic Systems"),
      desc: t("verticals.electronics.desc", "Flight computers, data acquisition and avionics stacks."),
      img: electronicPng,
      link: "/category/eletronic-systems",
    },
    {
      name: t("verticals.cubesats.name", "CubeSat Deployer System"),
      desc: t("verticals.cubesats.desc", "Reliable deployment systems for small satellites."),
      img: cubesatsPng,
      link: "/category/cubesat-deployer-system",
    },
  ];

  const stats = [
    { img: liquidEngineImage, value: "64", label: t("stats.engines", "Liquid Rocket Engines Tested") },
    { img: differentCombustionImage, value: "18", label: t("stats.chambers", "Different Combustion Chambers Tested") },
    { img: cumulativeImage, value: "220s", label: t("stats.burnTime", "Cumulative Bipropellant Burn Time") },
  ];

  const t8 = [
    {
      img: t8a,
      code: "T8A",
      link: "/services/t8#t8a",
      title: t("t8.t8a.title", "Mission Control and Monitoring"),
      desc: t("t8.t8a.desc", "Centralized control and real-time monitoring of all test operations through our proprietary software and in-house data acquisition system."),
    },
    {
      img: t8b,
      code: "T8B",
      link: "/services/t8#t8b",
      title: t("t8.t8b.title", "Test Stand and Control Systems"),
      desc: t("t8.t8b.desc", "Dedicated infrastructure for hydraulic, pneumatic, and electronic test systems, equipped with advanced safety mechanisms and CCTV monitoring."),
    },
    {
      img: t8c,
      code: "T8C",
      link: "/services/t8#t8c",
      title: t("t8.t8c.title", "HTP Concentration Laboratory"),
      desc: t("t8.t8c.desc", "In-house production and quality testing of high-concentration hydrogen peroxide (HTP), ensuring reliability and performance standards."),
    },
  ];

  return (
    <PageShell>
      {/* HERO */}
      <section className="relative min-h-[calc(100vh+100px)] flex items-end overflow-hidden -mt-20 pt-20">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />
        <div className="relative container-bizu pb-20 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-end">
          <h1 className="display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] animate-fade-up">
            {t("hero.title1", "Space technologies")}
            <br />
            {t("hero.title2", "for the launch")}
            <br />
            {t("hero.title3", "industry")}
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-md lg:text-right ml-auto animate-fade-up [animation-delay:.2s]">
            {t("hero.description", "BIZU Space is a vertical developer of rocket and satellite technologies for the space industry.")}
          </p>
        </div>
      </section>

      <SplitSection
        image={pururuca}
        title={t("propulsion.title", "High performance chemical propulsion")}
        description={t("propulsion.description", "Based on storable propellants, BIZU Space designs and manufacture liquid rocket engines for launch vehicle upper stages, reaction control systems, training rockets, OTVs and drone/aerial target accelerators.")}
        href="/category/propulsion-systems"
        tone="amber"
      />

      <SplitSection
        image={ftlCta}
        title={t("ftl.title", "LIQUID TRAINING ROCKET (FTL)")}
        description={t("ftl.description", "The Liquid Training Rocket FTL-Perseu uses Arion as its main engine. It is the first ever Brazilian rocket that uses only liquid propulsion to lift off and fly.")}
        href="/services/ftl"
        tone="amber"
        inverted
      />

      {/* STATS */}
      <section className="container-bizu py-24 lg:py-32 relative">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[120vmin] h-[120vmin] pointer-events-none">
          <div className="orbit-ring" />
          <div
            className="orbit-ring"
            style={{ inset: "10%", animationDuration: "120s", animationDirection: "reverse" }}
          />
        </div>
        <Reveal effect="clip" className="text-center max-w-4xl mx-auto mb-16 relative">
          <h2 className="display text-4xl md:text-6xl">
            {t("stats.title1", "Rocket technology is hard,")}
            <br />
            {t("stats.title2", "that's why we do it!")}
          </h2>
          <p className="mt-6 italic text-muted-foreground">
            {t("stats.description", "Numbers until now, validated through real testing.")}
          </p>
        </Reveal>
        <Reveal stagger className="grid md:grid-cols-3 gap-1 relative">
          {stats.map((s) => (
            <div key={s.label} className="relative aspect-[4/5] overflow-hidden group">
              <img
                src={s.img}
                alt={s.label}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                width={800}
                height={1000}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="display text-6xl md:text-7xl mb-2">
                  <CountUpNumber value={s.value} />
                </div>
                <div className="text-sm text-white/90">{s.label}</div>
              </div>
            </div>
          ))}
        </Reveal>
      </section>

      {/* T8 TEST FACILITY */}
      <section className="container-bizu py-24">
        <Reveal effect="scale" className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="display text-4xl md:text-6xl mt-4">{t("t8.title", "T8 Test Facility")}</h2>
          <p className="mt-5 text-muted-foreground">
            {t("t8.description", "A private, totally in-house developed test facility for liquid and/or hybrid rocket engines up to 20 kN thrust.")}
          </p>
        </Reveal>
        <Reveal stagger className="grid md:grid-cols-3 gap-6">
          {t8.map((c) => (
            <article key={c.code} className="group flex flex-col h-full">
              <div className="aspect-square overflow-hidden mb-6 shrink-0">
                <img
                  src={c.img}
                  alt={c.title}
                  loading="lazy"
                  width={900}
                  height={900}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                />
              </div>
              <div className="flex flex-col flex-grow">
                <h3 className="display text-xl mb-3">
                  {c.code} — {c.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-grow">{c.desc}</p>
                <Link
                  to={c.link}
                  className="inline-flex items-center gap-2 text-sm font-medium hover:text-accent transition-colors mt-auto"
                >
                  {t("t8.moreAbout", "More about")} {c.code} <span aria-hidden>→</span>
                </Link>
              </div>
            </article>
          ))}
        </Reveal>
      </section>

      <SplitSection
        video={mlbrVideo}
        eyebrow={t("mlbr.tag", "Mission")}
        title={t("mlbr.title", "Brazilian Microlauncher")}
        description={t("mlbr.description", "Developed by a group of 10 companies, the MLBR — Brazilian Microlauncher is a Brazilian microlauncher able to insert payloads into low earth orbit. A strategic project funded by FINEP (Financier of Studies and Projects) and AEB (Brazilian Space Agency).")}
        href="/mlbr"
        tone="blue"
      />

      <SplitSection
        video={verticalTechVideo}
        title={t("verticalTech.title", "Vertical technology capability")}
        description={t("verticalTech.description", "BIZU Space designs, projects, manufactures and tests its technology in-house. From electronic systems, software, propellant tanks, HTP and valves to rocket engines and CubeSat systems.")}
        href="/products"
        tone="warm"
      />

      {/* TECHNOLOGY VERTICALS */}
      <section className="container-bizu border-t border-border py-24">
        <Reveal
          effect="left"
          className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end"
        >
          <div>
            <h2 className="display mt-4 text-4xl md:text-5xl">{t("verticals.title", "Our Technology Verticals")}</h2>
          </div>

          <Link
            to="/products"
            className="group inline-flex items-center gap-3 text-[15px] font-semibold uppercase tracking-[0.18em] text-white transition hover:text-accent"
          >
            {t("verticals.explore", "Explore products")}
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
        </Reveal>
        <Reveal stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {verticals.map((v) => (
            <Link key={v.name} to={v.link} className="group relative overflow-hidden">
              <div className="relative aspect-[4/6] overflow-hidden">
                <img
                  src={v.img}
                  alt={v.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />

                {/* Conteúdo */}
                <div className="absolute bottom-8 left-8 right-8 flex items-end gap-4">
                  <h3 className="display flex-1 text-2xl text-white">{v.name}</h3>

                  <div className="flex h-8 w-8 min-h-8 min-w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent text-black transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </Reveal>
      </section>

      <CtaSection
        image={ctaImg}
        title={t("cta.title", "Let's make rocket technology!")}
        subtitle={t("cta.subtitle", "Contact us to discuss your needs.")}
        buttonText={t("cta.button", "Contact us")}
        href="/contact"
      />

      {/* PARTNERS */}
      <section className="border-y border-border py-16 overflow-hidden">
        <div className="container-bizu overflow-hidden">
          <p className="section-tag justify-center text-center mb-10">
            {t("partners.tag", "Partnerships that accelerate results")}
          </p>

          <div className="relative w-full overflow-hidden">
            <div className="flex w-fit animate-ticker items-center gap-6">
              {[...partnerLogos, ...partnerLogos].map((logo, index) => (
                <div
                  key={index}
                  className="flex h-24 w-48 shrink-0 items-center justify-center rounded-2xl bg-white px-8 shadow-sm transition-transform hover:scale-105"
                >
                  {logo.includes("finep") ? (
                    <a href="https://www.finep.gov.br/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full h-full">
                      <img
                        src={logo}
                        alt="Partner"
                        className="max-h-14 w-auto object-contain mix-blend-multiply"
                        loading="lazy"
                      />
                    </a>
                  ) : (
                    <img
                      src={logo}
                      alt="Partner"
                      className="max-h-14 w-auto object-contain mix-blend-multiply"
                      loading="lazy"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
